import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, Pencil, Trash2, FlaskConical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "", city: "", institution: "", specialty: "Cardiology",
  years: 10, publications: 20, gcp_expiry: "2027-01-01",
};

export default function AdminCroInvestigators() {
  const [rows, setRows] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [search, setSearch] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/cro-investigators", {
        params: {
          q: search || undefined,
          specialty: specialtyFilter === "all" ? undefined : specialtyFilter,
        },
      });
      setRows(data.investigators || []);
      setSpecialties(data.specialties || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [search, specialtyFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (pi) => {
    setEditing(pi);
    setForm({
      name: pi.name, city: pi.city, institution: pi.institution,
      specialty: pi.specialty, years: pi.years, publications: pi.publications,
      gcp_expiry: pi.gcp_expiry,
    });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        years: Number(form.years),
        publications: Number(form.publications),
      };
      if (editing) {
        await api.patch(`/admin/cro-investigators/${editing.id}`, payload);
        toast.success(`Updated ${form.name}`);
      } else {
        await api.post("/admin/cro-investigators", payload);
        toast.success(`Added ${form.name}`);
      }
      setOpen(false); setEditing(null); setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (pi) => {
    if (!window.confirm(`Remove ${pi.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/cro-investigators/${pi.id}`);
      toast.success(`Removed ${pi.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const platinum = rows.filter((r) => r.tier === "Platinum").length;
    const expiring = rows.filter((r) => r.gcp_status === "expiring" || r.gcp_status === "expired").length;
    const avg = total ? Math.round(rows.reduce((s, r) => s + (r.score || 0), 0) / total) : 0;
    return { total, platinum, expiring, avg };
  }, [rows]);

  return (
    <AppShell
      pageTitle="CRO Investigator Editor"
      pageDescription="Add, edit, and retire Principal Investigators that power the CRO Insights investigator network — no DB access required."
    >
      {/* Top strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-pi-stats">
        <StatPill label="Investigators" value={stats.total} />
        <StatPill label="Platinum tier" value={stats.platinum} tone="emerald" />
        <StatPill label="GCP expiring" value={stats.expiring} tone="amber" />
        <StatPill label="Avg score" value={stats.avg} tone="sky" />
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search name, institution or city…"
                 className="pl-7" data-testid="admin-pi-search" />
        </div>
        <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
          <SelectTrigger className="w-[180px]" data-testid="admin-pi-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All specialties</SelectItem>
            {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={openCreate} data-testid="admin-pi-add">
          <Plus size={14} className="mr-1" /> Add PI
        </Button>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Institution · City</TableHead>
              <TableHead>Specialty</TableHead>
              <TableHead>Years · Pubs</TableHead>
              <TableHead>GCP</TableHead>
              <TableHead>Score</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">No investigators match this filter.</TableCell></TableRow>
            ) : rows.map((pi) => (
              <TableRow key={pi.id} data-testid={`admin-pi-row-${pi.id}`}>
                <TableCell className="font-medium text-primary">{pi.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{pi.institution} · {pi.city}</TableCell>
                <TableCell><Badge variant="outline">{pi.specialty}</Badge></TableCell>
                <TableCell className="text-sm">{pi.years}y · {pi.publications}</TableCell>
                <TableCell>
                  <GcpBadge status={pi.gcp_status} days={pi.gcp_days_to_expiry} expiry={pi.gcp_expiry} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-base text-primary">{pi.score}</span>
                    <Badge variant="outline" data-testid={`admin-pi-tier-${pi.id}`}>{pi.tier}</Badge>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(pi)} data-testid={`admin-pi-edit-${pi.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(pi)} data-testid={`admin-pi-delete-${pi.id}`}>
                    <Trash2 size={14} className="text-rose-700" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-pi-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical size={16} /> {editing ? "Edit investigator" : "Add new investigator"}
            </DialogTitle>
            <DialogDescription>
              Changes are reflected immediately in the Investigator Network view used across the CRO Insights module.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-2 mt-2">
            <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-pi-name" />
            <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="admin-pi-city" />
            <Field label="Institution" value={form.institution} onChange={(v) => setForm({ ...form, institution: v })} className="md:col-span-2" testid="admin-pi-institution" />
            <div>
              <Label>Specialty</Label>
              <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
                <SelectTrigger className="mt-1" data-testid="admin-pi-specialty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="GCP cert expiry (YYYY-MM-DD)" value={form.gcp_expiry} onChange={(v) => setForm({ ...form, gcp_expiry: v })} placeholder="2027-01-01" testid="admin-pi-gcp" />
            <Field label="Years of experience" type="number" value={form.years} onChange={(v) => setForm({ ...form, years: v })} testid="admin-pi-years" />
            <Field label="Peer-reviewed publications" type="number" value={form.publications} onChange={(v) => setForm({ ...form, publications: v })} testid="admin-pi-pubs" />
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.name.trim() || !form.institution.trim()} data-testid="admin-pi-save">
              {saving ? "Saving…" : editing ? "Save changes" : "Create PI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({ label, value, onChange, type = "text", className = "", placeholder, testid }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <Input type={type} value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             className="mt-1" data-testid={testid} />
    </div>
  );
}

function StatPill({ label, value, tone = "primary" }) {
  const tones = {
    primary: "border-primary/15 bg-primary/5 text-primary",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="mt-1 font-serif text-3xl">{value}</div>
    </div>
  );
}

function GcpBadge({ status, days, expiry }) {
  if (status === "expired") return <Badge className="bg-rose-100 text-rose-800 border-rose-200">Expired · {expiry}</Badge>;
  if (status === "expiring") return <Badge className="bg-amber-100 text-amber-800 border-amber-200">In {days}d · {expiry}</Badge>;
  return <Badge variant="outline">{expiry}</Badge>;
}
